import {
  CheckoutAppSelector,
  useAppSelector,
  RecoverableTerminalErrorsState,
  setRecoverableFlowStatus,
  useAppDispatch,
  setCardsAndBankAccounts,
  useInitiateInstallmentPaymentMutation,
  setRecoverableFlowTraceId,
  usePreferredCard,
  selectRecoverableDeclinesPaymentMethod,
  setCardForOverdueACH,
  setPreferredPaymentMethod,
  useApplePayFeature,
  useApplePayState,
  useAppRouter,
  usePreferredBankAccount,
  usePreferredPaymentMethod,
  useSelectedPaymentMethod,
  useShouldSetApplePayAsPurchaseMethod,
  ApplePayState,
  useCheckoutState,
  useCohort,
  setCashAppPayDetails,
  setCashAppPay,
} from '..'
import { CardBrand, CardFlowType, isCashAppPay, isSavedCard, OverdueNotification, PaymentMethodType, RecoverableTerminalErrorStatus } from '@afterpay/types'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'
import { Text } from '@afterpaytouch/core'
import { Cohort, ErrorCode, isFetchBaseQueryError, OrderResponse, OverduePaymentResponse } from '@afterpaytouch/portal-api'
import { getAllScheduleIds } from './helper'
import { ApplePayError, checkoutErrors, Features, InitiateMutationResponse, RecoverableTerminalErrorsNotification, TerminalErrorCode } from '../../model'
import { AppLink, Trans } from '../../component'
import { Route } from '../../config/router'
import { useCheckoutSizes } from '../../utils/responsive'
import { createSelector, SerializedError } from '@reduxjs/toolkit'
import { useNonInitialEffect } from '../../utils/hooks'
import { setRecoverableFlowPageReady } from './reducer'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { useTranslation } from '../../utils/convergence'
import { useFlag } from '@afterpaytouch/feature'
import { isUndefinedOrEmpty } from '@afterpay/utils'

/* Selectors */
export const recoverableTerminalErrorsStateSelector: CheckoutAppSelector<RecoverableTerminalErrorsState> = (state) => state.recoverableTerminalErrors
export const recoverableFlowTraceIdSelector: CheckoutAppSelector<string> = createSelector(
  recoverableTerminalErrorsStateSelector,
  (state) => state?.traceId ?? ''
)

/* Helper for non hooks */
export const isRecoverableFlow = (status: RecoverableTerminalErrorStatus): boolean =>
  status > RecoverableTerminalErrorStatus.NotStarted && status < RecoverableTerminalErrorStatus.Finished

/* React Hooks */
export const useIsRecoverableFlow = (): boolean => {
  const { status } = useAppSelector(recoverableTerminalErrorsStateSelector)
  return isRecoverableFlow(status)
}

export const useIsOverduePaymentsFlow = (): boolean => {
  const { error } = useCheckoutState()
  const isRecoverableFlow = useIsRecoverableFlow()

  return error?.errorCode === ErrorCode.OverduePaymentsCheckoutPaymentEligible && isRecoverableFlow
}

export const useIsRecoverableFlowPageReady = (): boolean => {
  const { isPageReady } = useAppSelector(recoverableTerminalErrorsStateSelector)
  return isPageReady
}

export const useRecoverableTerminalErrorsState = (): RecoverableTerminalErrorsState => useAppSelector(recoverableTerminalErrorsStateSelector)

// @ts-expect-error: OPERATION BLEED STOPPER
export const useRecoverableFlowSelectedOrder = (): OrderResponse | OverduePaymentResponse => useRecoverableTerminalErrorsState().selectedOrder

export const useHandleInitiateMutation = (isQuerySuccess: boolean): InitiateMutationResponse => {
  const [initiateInstallmentPayments, { data, isLoading: isInitiateLoading, isSuccess: isInitiateSuccess, isError }] = useInitiateInstallmentPaymentMutation()
  const selectedOrder = useRecoverableFlowSelectedOrder()

  useNonInitialEffect(() => {
    if (typeof selectedOrder === 'undefined') {
      return
    }

    if (isQuerySuccess) {
      initiateInstallmentPayments({
        paymentScheduleIds: getAllScheduleIds(selectedOrder),
        flow: CardFlowType.RECOVERY_ONFILE,
      })
    }
  }, [selectedOrder, isQuerySuccess])

  return {
    // @ts-expect-error: OPERATION BLEED STOPPER
    data,
    isInitiateLoading,
    isInitiateSuccess,
    isError,
  }
}

export const useHandlePaymentMethodsSetup = (initiatePayload: InitiateMutationResponse, enabledCardTypes: CardBrand[] = []): void => {
  const { data, isInitiateLoading: isLoading, isInitiateSuccess } = initiatePayload
  const { status } = useRecoverableTerminalErrorsState()
  const dispatch = useAppDispatch()
  const cohort = useCohort()
  const { savedBankAccounts, savedCards, traceId, cashAppPay } = data ?? {}

  useEffect(() => {
    if (isInitiateSuccess && !isLoading && status === RecoverableTerminalErrorStatus.InitiatedInstallmentPayment) {
      // set up preferred cards and bank accounts in store for topaz
      dispatch(
        setCardsAndBankAccounts({
          savedCards,
          savedBankAccounts,
          enabledCardTypes,
        })
      )

      // set up cash app pay if user relies on cash app pay and data is valid
      const cashAppPayDetails = cashAppPay?.[0]
      if (cohort === Cohort.CashOnly && isCashAppPay(cashAppPayDetails)) {
        dispatch(setCashAppPayDetails(cashAppPayDetails))
        const validCashAppPay = cashAppPay?.filter((cashAppPay) => isCashAppPay(cashAppPay))
        dispatch(setCashAppPay(validCashAppPay))
      }

      dispatch(setRecoverableFlowTraceId(traceId))
      dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.HandlePurchaseMethods))
    }
  }, [isInitiateSuccess, isLoading, status, dispatch, enabledCardTypes, savedBankAccounts, savedCards, traceId])
}

export const useRecoverableNotificationMsg = (): RecoverableTerminalErrorsNotification => {
  const { switched, reason, valid } = usePreferredCard() ?? {}
  const isRecoverableFlow = useIsRecoverableFlow()
  const { textSize, smallTextSize } = useCheckoutSizes()
  const { t } = useTranslation()

  const notificationMsg = useMemo(() => {
    const messageOptions = switched ? 'switched' : 'cannotSwitch'

    if (reason === null || !isRecoverableFlow) {
      return null
    }

    return {
      heading: (
        <Text size={textSize} renderAs='div' bold>
          {t(`recoverableTerminalError:error.${reason}.heading`, { context: messageOptions })}
        </Text>
      ),
      message: (
        <Text size={smallTextSize} renderAs='div'>
          <Trans
            i18nKey={`recoverableTerminalError:error.${reason}.message`}
            components={{
              msgLink: <AppLink underline noMargin inline route={Route.ADD_PAYMENT_METHOD} />,
            }}
            context={messageOptions}
          />
        </Text>
      ),
      severe: !valid,
    }
  }, [reason, switched, t, textSize, smallTextSize, isRecoverableFlow, valid])

  // @ts-expect-error: OPERATION BLEED STOPPER
  return notificationMsg
}

export const useHandlePurchaseMethods = (setNotification: Dispatch<SetStateAction<OverdueNotification>>): void => {
  const dispatch = useAppDispatch()
  const { status } = useRecoverableTerminalErrorsState()
  const preferredPaymentMethod = usePreferredPaymentMethod()
  const { account } = usePreferredBankAccount() ?? {}
  const isApplePayEnabled = useApplePayFeature()
  const { isEnabledForDevice } = useApplePayState()
  const router = useAppRouter()
  const shouldUseApplePay = useShouldSetApplePayAsPurchaseMethod()
  const cohort = useCohort()
  const isCashOnlyCohort = cohort === Cohort.CashOnly

  const { card, switched, reason, valid } = usePreferredCard() ?? {}
  const notificationMsg = useRecoverableNotificationMsg()

  useEffect(() => {
    const handlePurchaseMethods = async (): Promise<void> => {
      try {
        // mimics Summary page's doStartCheckout
        if (shouldUseApplePay) {
          await dispatch(selectRecoverableDeclinesPaymentMethod({ type: PaymentMethodType.APPLE_PAY }))
          dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
          dispatch(setRecoverableFlowPageReady(true))
        } else if (preferredPaymentMethod === PaymentMethodType.ACH_BANK_ACCOUNT && card !== null) {
          // if ACH is the preferred payment method, automatically switch to card.
          // If ACH is set up, show ACH as a payment method but disabled
          dispatch(
            setCardForOverdueACH({
              // @ts-expect-error: OPERATION BLEED STOPPER
              card,
              switched: true,
              reason: 'achNotAllowed',
              // @ts-expect-error: OPERATION BLEED STOPPER
              valid: card.error === null,
            })
          )

          dispatch(setPreferredPaymentMethod(PaymentMethodType.CREDIT_CARD))
        } else if (card !== null) {
          await dispatch(
            selectRecoverableDeclinesPaymentMethod(
              {
                // @ts-expect-error: OPERATION BLEED STOPPER
                card: { token: card.token },
                type: PaymentMethodType.CREDIT_CARD,
              },
              false
            )
          )

          if (reason !== null) {
            setNotification(notificationMsg)
          }
          dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
          dispatch(setRecoverableFlowPageReady(true))
        } else {
          if (isCashOnlyCohort) {
            dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Started))
            dispatch(setRecoverableFlowPageReady(true))
          } else {
            dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.LackOfEligiblePaymentMethod))
            router.push(Route.ADD_PAYMENT_METHOD)
          }
        }
      } catch (e) {
        dispatch(setRecoverableFlowStatus(RecoverableTerminalErrorStatus.Error))
        if (e instanceof Error) {
          capture.error(e)
        }
        router.push(Route.TERMINAL_ERROR)
      }
    }

    if (status === RecoverableTerminalErrorStatus.HandlePurchaseMethods) {
      handlePurchaseMethods()
    }

    // Removed 'router' from dep array as it was causing rerenders
  }, [
    card,
    dispatch,
    shouldUseApplePay,
    status,
    isApplePayEnabled.new,
    isEnabledForDevice,
    preferredPaymentMethod,
    reason,
    switched,
    valid,
    account,
    notificationMsg,
  ])
}

export const useHandleRecoverableSetup = (): RecoverableTerminalErrorsNotification | null => {
  const { card, reason, valid } = usePreferredCard() ?? {}
  const { textSize } = useCheckoutSizes()
  const selectedPaymentMethod = useSelectedPaymentMethod()
  // @ts-expect-error: OPERATION BLEED STOPPER
  const [notification, setNotification] = useState<RecoverableTerminalErrorsNotification>(null)
  const notificationMsg = useRecoverableNotificationMsg()

  useEffect(() => {
    // To persist the AMEX warning when there are no valid cards
    // and the payment method hasn't changed
    if (
      !valid &&
      selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD &&
      'card' in selectedPaymentMethod &&
      // @ts-expect-error: OPERATION BLEED STOPPER
      isSavedCard(selectedPaymentMethod?.card) &&
      // @ts-expect-error: OPERATION BLEED STOPPER
      selectedPaymentMethod.card.token === card.token &&
      reason !== null
    ) {
      setNotification(notificationMsg)
    }
  }, [card, valid, reason, selectedPaymentMethod, textSize])

  useHandlePurchaseMethods(setNotification)

  return notification
}

export const useIsRecoverableFlowReadyToConfirm = (): boolean => {
  const { status } = useRecoverableTerminalErrorsState()

  return status === RecoverableTerminalErrorStatus.Started
}

export const useRecoverableFlowTraceId = (): string => useAppSelector(recoverableFlowTraceIdSelector)

export const useSetErrorKeys = (
  confirmPayError: FetchBaseQueryError | SerializedError | undefined,
  setErrorKey: React.Dispatch<React.SetStateAction<string>>
): void => {
  useEffect(() => {
    let currentErrorKey
    if (isFetchBaseQueryError(confirmPayError)) {
      currentErrorKey = checkoutErrors[confirmPayError?.data?.errorCode ?? '']
    }
    setErrorKey(currentErrorKey)
  }, [confirmPayError, setErrorKey])
}

interface SetErrorFieldParams {
  heading?: string
  message: string
  severe?: boolean
}

export const useSetErrorFields = (
  isConfirmPayError: boolean,
  setErrorMessage: React.Dispatch<React.SetStateAction<string>>,
  setClicked: React.Dispatch<React.SetStateAction<boolean>>,
  setNotification: React.Dispatch<React.SetStateAction<RecoverableTerminalErrorsNotification | null>>,
  errorKey: string | undefined,
  applepay: ApplePayState
): void => {
  const { t } = useTranslation()

  useEffect(() => {
    const setErrorFields = ({ heading, message, severe = false }: SetErrorFieldParams): void => {
      setClicked(false)
      if (!isUndefinedOrEmpty(heading)) {
        setNotification({ heading, message, severe })
        setErrorMessage('')
      } else {
        setNotification(null)
        setErrorMessage(message)
      }
    }

    const getErrorKeyFromApplePayError = (): string => {
      switch (applepay.error) {
        case ApplePayError.InvalidCardType:
          return 'prepaidCard'
        default:
          return 'applePay'
      }
    }

    const hasErrorKey = typeof errorKey !== 'undefined'
    const isMappedError = isConfirmPayError && hasErrorKey
    const isApplepayError = typeof applepay.error !== 'undefined'
    const isUnmappedConfirmPayError = isConfirmPayError && !hasErrorKey

    const currentErrorKey = isMappedError ? errorKey : getErrorKeyFromApplePayError()
    const heading = t(`summary:error.${currentErrorKey}.heading`, { defaultValue: '' })
    const message = t(`summary:error.${currentErrorKey}.message`)

    if (isMappedError) {
      setErrorFields({
        ...(!isUndefinedOrEmpty(heading) && { heading }),
        message,
        severe: false,
      })
    } else if (isApplepayError) {
      setErrorFields({
        ...(!isUndefinedOrEmpty(heading) && { heading }),
        message,
        severe: true,
      })
    } else if (isUnmappedConfirmPayError) {
      // unmapped errors
      setErrorFields({
        message: t('payments:customPayment.payment.error.messages.default'),
      })
    }
  }, [isConfirmPayError, applepay.error, errorKey])
}

export const useIsOrderAmountExceedsSpendCapEnabled = (): boolean => {
  const cohort = useCohort()
  const isFeatureFlagEnabled = useFlag(Features.ORDER_AMOUNT_EXCEEDS_SPEND_CAP)
  const isCashOnly = cohort === Cohort.CashOnly
  const isOrderAmountExceedsSpendCapEnabled = isFeatureFlagEnabled && !isCashOnly

  return isOrderAmountExceedsSpendCapEnabled
}

export const useIsOrderAmountExceedsSpendCap = (
  errorCode: TerminalErrorCode
): {
  isOrderAmountExceedsSpendCap: boolean
  isOrderAmountExceedsSpendCapAndNotEnabled: boolean
} => {
  const isOrderAmountExceedsSpendCapEnabled = useIsOrderAmountExceedsSpendCapEnabled()
  const isOrderAmountExceedsSpendCapError = errorCode === TerminalErrorCode.OrderAmountExceedsSpendCap
  const isOrderAmountExceedsSpendCap = isOrderAmountExceedsSpendCapEnabled && isOrderAmountExceedsSpendCapError
  const isOrderAmountExceedsSpendCapAndNotEnabled = isOrderAmountExceedsSpendCapError && !isOrderAmountExceedsSpendCapEnabled
  return { isOrderAmountExceedsSpendCap, isOrderAmountExceedsSpendCapAndNotEnabled }
}
